// Admin API helper — uses access token from AuthContext (stored in memory only)
const BASE = process.env.REACT_APP_BACKEND_URL;

export class AdminApi {
    constructor() {
        this.token = null;
        this.onUnauthorized = null;
    }
    setToken(t) {
        this.token = t;
    }
    getToken() {
        return this.token;
    }
    setUnauthorizedHandler(fn) {
        this.onUnauthorized = fn;
    }

    async _request(path, options = {}) {
        const headers = {
            ...(options.headers || {}),
        };
        if (!(options.body instanceof FormData)) {
            headers["Content-Type"] = "application/json";
        }
        if (this.token) headers["Authorization"] = `Bearer ${this.token}`;

        const res = await fetch(`${BASE}${path}`, {
            ...options,
            credentials: "include",
            headers,
        });
        if (res.status === 401 && this.onUnauthorized) {
            this.onUnauthorized();
        }
        const ct = res.headers.get("content-type") || "";
        let body;
        if (ct.includes("application/json")) body = await res.json().catch(() => ({}));
        else body = await res.text();
        if (!res.ok) {
            const detail = (body && body.detail) || `Request failed (${res.status})`;
            const msg = typeof detail === "string" ? detail : JSON.stringify(detail);
            const err = new Error(msg);
            err.status = res.status;
            err.data = body;
            throw err;
        }
        return body;
    }

    get(path) { return this._request(path); }
    post(path, body) { return this._request(path, { method: "POST", body: body instanceof FormData ? body : JSON.stringify(body || {}) }); }
    put(path, body) { return this._request(path, { method: "PUT", body: JSON.stringify(body || {}) }); }
    del(path) { return this._request(path, { method: "DELETE" }); }

    // Auth
    login(email, password) { return this.post("/api/admin/auth/login", { email, password }); }
    refresh() { return this._request("/api/admin/auth/refresh", { method: "POST" }); }
    logout() { return this._request("/api/admin/auth/logout", { method: "POST" }); }
    me() { return this.get("/api/admin/auth/me"); }
    changePassword(payload) { return this.post("/api/admin/auth/change-password", payload); }

    // Dashboard
    dashboard() { return this.get("/api/admin/dashboard"); }
    auditLog(params = {}) {
        const q = new URLSearchParams(params).toString();
        return this.get(`/api/admin/audit-log${q ? `?${q}` : ""}`);
    }

    // Resources (generic)
    list(resource, params = {}) {
        const q = new URLSearchParams(params).toString();
        return this.get(`/api/admin/${resource}${q ? `?${q}` : ""}`);
    }
    getOne(resource, id) { return this.get(`/api/admin/${resource}/${id}`); }
    create(resource, payload) { return this.post(`/api/admin/${resource}`, payload); }
    update(resource, id, payload) { return this.put(`/api/admin/${resource}/${id}`, payload); }
    remove(resource, id) { return this.del(`/api/admin/${resource}/${id}`); }

    // Specific actions
    toggleStock(id) { return this.put(`/api/admin/products/${id}/toggle-stock`); }
    toggleFeatured(id) { return this.put(`/api/admin/products/${id}/toggle-featured`); }
    togglePublish(id) { return this.put(`/api/admin/blog/${id}/publish`); }
    approveReview(id) { return this.put(`/api/admin/reviews/${id}/approve`); }
    rejectReview(id) { return this.put(`/api/admin/reviews/${id}/reject`); }
    contactStatus(id, status) { return this.put(`/api/admin/contact-messages/${id}/status`, { status }); }
    reorderFaqs(items) { return this.put(`/api/admin/faqs/reorder`, { items }); }

    // Settings
    getSettings() { return this.get("/api/admin/site/settings"); }
    updateSettings(payload) { return this.put("/api/admin/site/settings", payload); }

    // Upload
    upload(file) {
        const fd = new FormData();
        fd.append("file", file);
        return this.post("/api/admin/uploads", fd);
    }

    // CSV export
    newsletterExportUrl() { return `${BASE}/api/admin/newsletter/export.csv`; }
}

export const adminApi = new AdminApi();
